import React, { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import strings from '../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../utils/axios';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import { languages } from '../../../../../utils/redux/reducer/authentication-slice';
import AddEdit from '../../../CommonPage/AddEdit';
import { EditAddOnCategoryForm } from './EditAddOnCategoryForm';
import { AlertColor } from '@mui/material';
import PageLoader from '../../../../../components/PageLoader';
import Notification from '../../../../../components/Notification';

const EditAddOnCategory: FC = () => {
  const { id } = useParams() as any;
  const languageList = useSelector(languages) as any;

  const [pageResponse, setPageResponse] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({ display: false, severity: null, message: '' });

  const closeNotification = (value: boolean) => setMessage({ ...message, display: value });

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: (error.response.data?.error_description ?? error.response.data.message) ?? error.message,
    });
  };

  const getAddOnCategoryResponse = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.getAddOnCategoriesById(id));
      const container: any = {};
      for (let i = 0; i < languageList?.length; ++i) {
        let name = [`name_${languageList[i]?.value}`];
        container[name as any] = data.localizedAttributes[languageList[i]?.value]
          ? data.localizedAttributes[languageList[i]?.value].name
          : '';
      }
      setPageResponse({ ...data, ...container });
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, [id, languageList]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getAddOnCategoryResponse();
      }
    })();
  }, [getAddOnCategoryResponse, id]);

  const handleSetModel = useCallback(() => {
    const newArray = [...EditAddOnCategoryForm(strings, id)];

    if (languageList && languageList.length > 0) {
      languageList.forEach((language: any) => {
        const nameObject = { ...EditAddOnCategoryForm(strings, id)[3] };
        nameObject.field = `name_${language.value}`;
        nameObject.label = `${nameObject.label} (${language.text})`;
        newArray.push(nameObject);
      });
    }

    return newArray.filter((item: any) => item.field !== 'name');
  }, [languageList]);

  const pageConfig = {
    title: id ? strings.addOnCategory_title_edit : strings.addOnCategory_title_add,
    successMessage: id ? strings.common_update_message : strings.common_save_message,
    addEditApiUrl: id ? URLS.getAddOnCategoriesById(id) : URLS.addAddOnCategories,
    formModel: handleSetModel(),
    navigateToPage: routes.addOnCategoriesList,
    isCustomResponse: true,
  };

  const handleCustomResponse = (custom: any) => {
    const localizedAttributes = [] as any;
    languageList !== undefined &&
    languageList?.map((item: { text: string; value: string }) =>
      localizedAttributes.push({
        language: item.value,
        name: custom[`name_${item.value}`],
      }),
    );
    return {
      code: custom.code,
      isServiceChargeApplicable: custom.serviceChargeApplicable ?? false,
      localizedAttributes: localizedAttributes,
    };
  };

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <AddEdit
        pageLoading={isLoading}
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        handleCustomResponse={handleCustomResponse as any}
      />
    </>
  );
};
export default EditAddOnCategory;
